/**
 * Sunlight Legend Configuration
 *
 * Defines the legend configuration for sunlight hours visualization
 * Based on typical sun exposure gradients from shademap.app
 */

import type { LegendConfig } from '../AnalysisMap.types'

/**
 * Sunlight hours color gradient (from less sun to more sun)
 * Colors matched to the actual shademap library gradient visible on the map
 */
export const SUNLIGHT_HOURS_COLORS = [
  '#1e3a5f', // 0-2 hours - dark blue (minimal sun)
  '#2563a8', // 2-4 hours - medium blue
  '#4a9eff', // 4-6 hours - light blue
  '#7ec8e3', // 6-8 hours - cyan/light blue
  '#ffa500', // 8-10 hours - orange
  '#ff8c00', // 10-12 hours - darker orange
  '#ffeb3b', // 12-14 hours - yellow (maximum sun)
]

/**
 * Hour ranges corresponding to the colors
 */
export const SUNLIGHT_HOURS_RANGES = [0, 2, 4, 6, 8, 10, 12, 14]

/**
 * Sunlight legend configuration
 */
export const SUNLIGHT_LEGEND_CONFIG: Record<string, LegendConfig> = {
  'sunlight-hours': {
    title: 'Daily Sun Exposure',
    type: 'gradient',
    unit: 'hours',
    entries: [
      { color: SUNLIGHT_HOURS_COLORS[0], label: '0-2h' },
      { color: SUNLIGHT_HOURS_COLORS[1], label: '2-4h' },
      { color: SUNLIGHT_HOURS_COLORS[2], label: '4-6h' },
      { color: SUNLIGHT_HOURS_COLORS[3], label: '6-8h' },
      { color: SUNLIGHT_HOURS_COLORS[4], label: '8-10h' },
      { color: SUNLIGHT_HOURS_COLORS[5], label: '10-12h' },
      { color: SUNLIGHT_HOURS_COLORS[6], label: '12h+' },
    ],
  },
}

/**
 * Get legend config for sunlight hours layer
 */
export function getSunlightLegendConfig(
  layerId: string
): LegendConfig | undefined {
  return SUNLIGHT_LEGEND_CONFIG[layerId]
}

/**
 * Check if a layer has a sunlight legend
 */
export function hasSunlightLegend(layerId: string): boolean {
  return layerId in SUNLIGHT_LEGEND_CONFIG
}
